import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, ArrowDown } from "lucide-react";
import { BRAND } from "../config/brand";
import { useLang } from "../i18n/LangContext";

export const Hero = () => {
  const { t } = useLang();

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="hero"
      data-testid="section-hero"
      className="relative min-h-[100svh] w-full overflow-hidden grain flex items-center"
    >
      {/* Background image */}
      <div
        className="absolute inset-0 z-0 bg-cover bg-center scale-110"
        style={{ backgroundImage: `url(${BRAND.heroImage})` }}
      />
      {/* Overlays */}
      <div className="absolute inset-0 z-[1] bg-gradient-to-b from-black/85 via-black/70 to-black" />
      <div className="absolute inset-0 z-[1] bg-gradient-to-r from-black via-black/40 to-transparent" />
      <div className="red-glow z-[2]" style={{ top: "10%", right: "-15%" }} />
      <div className="red-glow z-[2]" style={{ bottom: "-30%", left: "-20%", opacity: 0.4 }} />

      <div className="relative z-10 mx-auto max-w-[1400px] px-6 lg:px-12 w-full pt-32 pb-24">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, scale: 0.85 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
              className="lg:hidden mb-10 flex justify-center"
            >
              <div className="relative">
                <img
                  src={BRAND.logo}
                  alt="LionPerformance"
                  className="relative w-64 h-64 sm:w-80 sm:h-80 object-contain"
                  style={{ filter: "url(#lp-remove-black)" }}
                  data-testid="hero-logo-mobile"
                />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              className="flex items-center gap-3 mb-8"
            >
              <span className="h-px w-10 bg-primary-red" />
              <span className="text-[10px] sm:text-xs font-bold uppercase tracking-[0.28em] text-zinc-400">
                {t("hero.eyebrow")}
              </span>
            </motion.div>

            <h1 className="font-display font-black leading-[0.95] tracking-tight">
              <motion.span
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 0.05, ease: [0.16, 1, 0.3, 1] }}
                className="block text-white text-4xl sm:text-5xl lg:text-6xl xl:text-7xl"
              >
                {t("hero.title1")}
              </motion.span>
              <motion.span
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 0.18, ease: [0.16, 1, 0.3, 1] }}
                className="block text-primary-red text-4xl sm:text-5xl lg:text-6xl xl:text-7xl mt-2"
              >
                {t("hero.title3")}
              </motion.span>
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.4 }}
              className="mt-8 max-w-xl font-body text-base lg:text-lg text-zinc-300 leading-relaxed"
            >
              {t("hero.subtitle")}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.55 }}
              className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4"
            >
              <button
                data-testid="hero-cta-primary"
                onClick={() => scrollTo("kontakt")}
                className="btn-primary group inline-flex items-center gap-3 px-8 py-4 bg-primary-red text-white text-sm font-bold uppercase tracking-[0.15em] hover:bg-[#B3000C] transition-colors"
              >
                {t("hero.cta1")}
                <ArrowRight
                  size={18}
                  className="transition-transform duration-300 group-hover:translate-x-1"
                />
              </button>
              <button
                data-testid="hero-cta-secondary"
                onClick={() => scrollTo("warum")}
                className="group inline-flex items-center gap-3 px-8 py-4 border border-white/20 text-white text-sm font-bold uppercase tracking-[0.15em] hover:bg-white/5 transition-colors"
              >
                {t("hero.cta2")}
                <ArrowDown
                  size={18}
                  className="transition-transform duration-300 group-hover:translate-y-1"
                />
              </button>
            </motion.div>

            {/* Trust bar — proof stack directly under CTAs */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.75 }}
              className="mt-12 pt-6 border-t border-white/10 grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-4 max-w-3xl"
              data-testid="hero-trustbar"
            >
              {(t("hero.trustbar") || []).map((item, i) => (
                <div
                  key={i}
                  className="flex items-start gap-2.5"
                  data-testid={`hero-trust-${i}`}
                >
                  <span className="mt-1.5 w-1 h-1 rounded-full bg-primary-red shrink-0" />
                  <span className="font-body text-xs sm:text-sm text-zinc-300 leading-tight tracking-wide">
                    {item}
                  </span>
                </div>
              ))}
            </motion.div>
          </div>

          {/* Logo on right */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.3 }}
            className="hidden lg:flex lg:col-span-5 items-center justify-center"
          >
            <div className="relative">
              {/* Red glow behind the logo */}
              <div
                className="absolute inset-0 -m-10 pointer-events-none"
                style={{
                  background:
                    "radial-gradient(circle at center, rgba(227,0,15,0.32) 0%, rgba(227,0,15,0.08) 45%, rgba(227,0,15,0) 70%)",
                  filter: "blur(40px)",
                }}
              />
              <img
                src={BRAND.logo}
                alt="LionPerformance"
                className="relative w-[520px] h-[520px] xl:w-[600px] xl:h-[600px] object-contain"
                style={{ filter: "url(#lp-remove-black)" }}
                data-testid="hero-logo"
              />
              <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 glass px-5 py-2 rounded-full whitespace-nowrap z-10">
                <span className="text-[10px] font-bold uppercase tracking-[0.3em] text-gold">
                  Elite Performance
                </span>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Scroll indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2, duration: 1 }}
          className="absolute bottom-6 left-6 lg:left-12 flex items-center gap-3"
        >
          <span className="text-[10px] font-bold uppercase tracking-[0.3em] text-zinc-500">
            {t("hero.scroll")}
          </span>
          <div className="w-8 h-px bg-zinc-500" />
        </motion.div>
      </div>
    </section>
  );
};
